import React from 'react';
import { Card, Typography, Tag, Dropdown, Button } from 'antd';
import {
    MoreOutlined, EyeOutlined, CheckOutlined, DeleteOutlined,
    InfoCircleOutlined, WarningOutlined, ExclamationCircleOutlined, AlertOutlined,
} from '@ant-design/icons';
import { InAppNotification, NotificationPriority } from '../types';
import dayjs from 'dayjs';

const { Text, Paragraph } = Typography;

const priorityColor = (p?: NotificationPriority) => {
    switch (p) {
        case 'CRITICAL': return '#ff4d4f';
        case 'ALERT':    return '#fa8c16';
        case 'WARNING':  return '#fadb14';
        default:         return '#1890ff';
    }
};

const priorityIcon = (p?: NotificationPriority) => {
    switch (p) {
        case 'CRITICAL': return <ExclamationCircleOutlined />;
        case 'ALERT':    return <AlertOutlined />;
        case 'WARNING':  return <WarningOutlined />;
        default:         return <InfoCircleOutlined />;
    }
};

interface Props {
    notification: InAppNotification;
    onMarkAsRead?: (id: number) => void;
    onDelete?: (id: number) => void;
    onClick?: (n: InAppNotification) => void;
}

export const NotificationItem: React.FC<Props> = ({ notification, onMarkAsRead, onDelete, onClick }) => {
    const menuItems = [
        {
            key: 'read',
            label: 'Mark as Read',
            icon: <EyeOutlined />,
            disabled: !!notification.read,
            onClick: () => notification.id && onMarkAsRead?.(notification.id),
        },
        { type: 'divider' as const },
        {
            key: 'delete',
            label: 'Delete',
            icon: <DeleteOutlined />,
            danger: true,
            onClick: () => notification.id && onDelete?.(notification.id),
        },
    ];

    return (
        <Card
            size="small"
            style={{
                marginBottom: 8,
                borderLeft: `4px solid ${priorityColor(notification.priority)}`,
                backgroundColor: !notification.read ? '#fafafa' : 'transparent',
                cursor: onClick ? 'pointer' : 'default',
            }}
            onClick={() => onClick?.(notification)}
            extra={
                <Dropdown menu={{ items: menuItems }} trigger={['click']} placement="bottomRight">
                    <Button type="text" icon={<MoreOutlined />} size="small" onClick={(e) => e.stopPropagation()} />
                </Dropdown>
            }
        >
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                <span style={{ color: priorityColor(notification.priority) }}>{priorityIcon(notification.priority)}</span>
                <Text strong style={{ flex: 1 }}>{notification.title}</Text>
                {!notification.read && (
                    <div style={{ width: 8, height: 8, borderRadius: '50%', backgroundColor: '#1890ff' }} />
                )}
            </div>
            <Paragraph style={{ fontSize: 13, color: '#666', marginBottom: 8 }} ellipsis={{ rows: 2 }}>
                {notification.message}
            </Paragraph>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <div style={{ display: 'flex', gap: 4 }}>
                    <Tag color={priorityColor(notification.priority)} style={{ fontSize: 11 }}>{notification.priority}</Tag>
                    {notification.category && <Tag style={{ fontSize: 11 }}>{notification.category}</Tag>}
                    {!notification.read && <Tag color="orange" style={{ fontSize: 11 }}>Unread</Tag>}
                </div>
                <Text type="secondary" style={{ fontSize: 11 }}>
                    {notification.createdAt ? dayjs(notification.createdAt).format('MMM DD, HH:mm') : ''}
                </Text>
            </div>
        </Card>
    );
};
